import {
  TimeRangeKey,
  TimeRangeLabel,
  TimeRangeType,
  TimestampRange
} from '../components/time-picker/types';

export abstract class TimeRange {
  public key: TimeRangeKey;
  public type: TimeRangeType;
  public from: Date;
  public to: Date;
  public dimension: string;
  public timezone: string;
  public utc = false;
  public isZoomed?: boolean;
  private _granularity: number;
  private _timerange: number;
  private _label: TimeRangeLabel | string;

  constructor(
    key: TimeRangeKey,
    type: TimeRangeType,
    from?: Date | number,
    to?: Date | number,
    dimension?: string
  ) {
    this.key = key;
    this.type = type;
    if (from) {
      this.from = from instanceof Date ? from : new Date(from);
    }
    if (to) {
      this.to = to instanceof Date ? to : new Date(to);
    }
    this.dimension = dimension;
    this.timerange = TimeRange.getTimerangeByKey(key);
  }

  public get granularity(): number {
    return this._granularity;
  }

  public set granularity(value: number) {
    this._granularity = value;
    if (this.key === TimeRangeKey.CUSTOM) {
      return;
    }
    if (this.fromTime) {
      this.fromTime -= this.fromTime % this.granularity;
    }
    if (this.toTime) {
      this.toTime -= this.toTime % this.granularity;
    }
  }

  get fromTime(): number {
    return this.from ? this.from.getTime() : null;
  }

  set fromTime(value: number) {
    this.from = new Date(value);
  }

  get toTime(): number {
    return this.to ? this.to.getTime() : null;
  }

  set toTime(value: number) {
    this.to = new Date(value);
  }

  // aliases for backward compatibility with the query params resolver
  get from_time(): number {
    return this.fromTime;
  }

  get to_time(): number {
    return this.toTime;
  }

  get timerange(): number {
    return this._timerange;
  }

  set timerange(value: number) {
    this._timerange = isNaN(value) ? null : value;
  }

  get label(): string {
    if (this._label) {
      return this._label;
    }
    switch (this.key) {
      case TimeRangeKey.LAST_1H:
        return TimeRangeLabel.LAST_1H;
      case TimeRangeKey.LAST_12H:
        return TimeRangeLabel.LAST_12H;
      case TimeRangeKey.LAST_1DAY:
        return TimeRangeLabel.LAST_DAY;
      case TimeRangeKey.LAST_1WEEK:
        return TimeRangeLabel.LAST_WEEK;
      case TimeRangeKey.MINUS_24H:
        return TimeRangeLabel.MINUS_24H;
      case TimeRangeKey.PLUS_24H:
        return TimeRangeLabel.PLUS_24H;
      case TimeRangeKey.CUSTOM:
        return TimeRangeLabel.CUSTOM;
      default:
        throw new Error(`Label not defined for ${this.key}`);
    }
  }

  set label(value: string) {
    this._label = value;
  }

  /**
   * Returns the time range interval in milliseconds
   */
  public static getTimerangeByKey(key: TimeRangeKey): number {
    switch (key) {
      case TimeRangeKey.LAST_1H:
      case TimeRangeKey.LAST_1H_CACHED:
        return 3600000;
      case TimeRangeKey.LAST_12H:
        return 43200000;
      case TimeRangeKey.LAST_1DAY:
        return 86400000;
      case TimeRangeKey.LAST_1WEEK:
        return 604800000;
      case TimeRangeKey.LAST_30DAYS:
        return 2592000000;
      case TimeRangeKey.MINUS_24H:
        return -86400000;
      case TimeRangeKey.PLUS_24H:
        return 86400000;
      case TimeRangeKey.CUSTOM:
        return 0;
      default:
        throw new Error(`Timerange not defined for ${key}`);
    }
  }

  public static fromJSON(json: { [key: string]: string }): TimeRange {
    let timeRange: TimeRange;
    switch (json.type) {
      case TimeRangeType.MOVING_WINDOW:
        timeRange = new MovingWindowTimeRange(<TimeRangeKey>json.key);
        break;
      case TimeRangeType.FIXED_RANGE:
        timeRange = new FixedTimeRange(
          <TimeRangeKey>json.key,
          Number(json.fromTime),
          Number(json.toTime)
        );
        break;
    }
    timeRange.dimension = json.dimension;
    timeRange.timezone = json.timezone;
    timeRange.label = json.label;
    return timeRange;
  }

  public toJSON(): { [key: string]: string } {
    const props = ['key', 'type', 'fromTime', 'toTime', 'timerange', 'dimension', 'timezone'];
    const json = {};
    props.filter(prop => !!this[prop]).forEach(prop => (json[prop] = this[prop]));

    return json;
  }

  public toFixedTimeRange(from: Date | number, to: Date | number): FixedTimeRange {
    return new FixedTimeRange(TimeRangeKey.CUSTOM, from, to);
  }

  public getDuration(): number {
    const range = this.getTimestampRange();
    return range.toTime - range.fromTime;
  }

  public matchesDuration(minDuration?: number, maxDuration?: number): boolean {
    const duration = this.getDuration();
    if (minDuration && duration < minDuration) {
      return false;
    }
    if (maxDuration && duration > maxDuration) {
      return false;
    }
    return true;
  }

  public snapToUTCDayBoundaries(): TimeRange {
    this.from.setUTCHours(0);
    this.from.setUTCMinutes(0);
    this.from.setUTCSeconds(0);
    this.from.setUTCMilliseconds(0);
    this.to.setUTCHours(0);
    this.to.setUTCMinutes(0);
    this.to.setUTCSeconds(0);
    this.to.setUTCMilliseconds(0);
    return this;
  }

  public minutesToZero(): TimeRange {
    this.from.setUTCMinutes(0);
    this.from.setUTCSeconds(0);
    this.from.setUTCMilliseconds(0);
    this.to.setUTCMinutes(0);
    this.to.setUTCSeconds(0);
    this.to.setUTCMilliseconds(0);
    return this;
  }

  /**
   * Returns the time range interval. If this.from of this.to are not set (eg: for moving window),
   * it will make a snapshot for the current moment in time in order to determine the time range.
   */
  public getTimestampRange(): TimestampRange {
    const fromTime =
      this.from instanceof Date
        ? this.from.getTime()
        : this.getStartDateNow() - TimeRange.getTimerangeByKey(this.key);
    const toTime = this.to instanceof Date ? this.to.getTime() : this.getStartDateNow();
    return { fromTime, toTime };
  }

  protected getStartDateNow(): number {
    return Date.now();
  }
}

export class FixedTimeRange extends TimeRange {
  constructor(key: TimeRangeKey, from?: Date | number, to?: Date | number) {
    super(key, TimeRangeType.FIXED_RANGE, from, to);
    this.timerange = null;
  }
}

export class MovingWindowTimeRange extends TimeRange {
  constructor(key: TimeRangeKey) {
    super(key, TimeRangeType.MOVING_WINDOW);
    this.from = null;
    this.to = null;
  }
}

export class MonitoringSessionTimeRange extends TimeRange {
  constructor(key: TimeRangeKey, from?: Date | number) {
    super(key, TimeRangeType.MONITORING_SESSION, from);
  }
}
